import axios from "axios";
const endPoint = process.env.REACT_APP_API_ENDPOINT;
const MULTIPART_TIMEOUT_MS = 60_000;

type ApiConfig = {
  headers?: Record<string, string>;
  timeout?: number;
  method?: string;
  withCredentials?: boolean;
};

const apiConfig = (flag = false): ApiConfig => {
  if (localStorage.getItem("accessToken")) {
    const headers: Record<string, string> = {
      Authorization: `bearer ${localStorage.getItem("accessToken")}`,
    };
    // For FormData, omit Content-Type so the browser sets multipart boundary.
    if (!flag) {
      headers["Content-Type"] = "application/json";
    }
    return {
      headers,
      ...(flag ? { timeout: MULTIPART_TIMEOUT_MS } : {}),
      method: "PUT,DELETE,POST,GET,OPTION",
    };
  }
  return { withCredentials: false };
};

/** Clearer message when multipart upload fails with no HTTP response. */
const enrichNetworkError = (error: any, isMultipart?: boolean) => {
  if (
    isMultipart &&
    error &&
    !error.response &&
    (error.code === "ERR_NETWORK" || error.message === "Network Error")
  ) {
    error.message =
      "Upload failed or connection was lost. Please try again with smaller images.";
  }
  return error;
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("accessToken");

      // Delay navigation to allow toast to show
      setTimeout(() => {
        window.location.href = "/login";
      }, 1500);
    }
    return Promise.reject(error);
  },
);

export const getApi = async (url?: string, params?: any) => {
  try {
    return await axios.get(`${endPoint}${url}`, {
      params: params,
      ...apiConfig(),
    });
  } catch (error) {
    return Promise.reject(error);
  }
};

export const postApi = async (url: string, apiData?: any, flag?: boolean) => {
  try {
    return await axios.post(`${endPoint}${url}`, apiData, apiConfig(flag));
  } catch (error) {
    return Promise.reject(enrichNetworkError(error, flag));
  }
};

/** Search candidate target medicines for Map (non-draft, not deleted, QC approved). */
export const getMapTargets = async (search?: string) =>
  getApi("/medicine/map-targets", search ? { search } : {});

/** Map a mistaken draft request onto an existing catalog medicine. */
export const postMapDraftRequest = async (
  requestId: string,
  targetMedicineId: string,
) => postApi(`/draft-medicine-request/${requestId}/map`, { targetMedicineId });

/** POST external catalog sync (full URL + x-api-key from env; not the panel API origin). */
export const postMedicineCatalogCsvSync = async () => {
  const url = process.env.REACT_APP_MEDICINE_CATALOG_SYNC_URL?.trim();
  const apiKey = process.env.REACT_APP_MEDICINE_CATALOG_SYNC_API_KEY?.trim();
  const token = localStorage.getItem("accessToken");
  if (!url || !apiKey) {
    return Promise.reject(
      new Error(
        "Medicine catalog sync is not configured. Set REACT_APP_MEDICINE_CATALOG_SYNC_URL and REACT_APP_MEDICINE_CATALOG_SYNC_API_KEY in .env",
      ),
    );
  }
  if (!token) {
    return Promise.reject(new Error("No access token"));
  }
  return axios.post(
    url,
    {},
    {
      headers: {
        Authorization: `bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json, text/plain, */*",
        "x-api-key": apiKey,
      },
    },
  );
};

export type AdminPanelMedicineSyncContentItem = {
  name: string;
  strength: string;
};

/** Omitted keys are not sent; only `masterMedicineId` and `rxProduct` are always included when calling the builder. */
export type AdminPanelMedicineSyncPayload = {
  masterMedicineId: string;
  hsnCode?: string;
  manufacturer?: string;
  content?: AdminPanelMedicineSyncContentItem[];
  packSize?: string;
  medicineName?: string;
  medicineStrength?: string;
  rxProduct: boolean;
  scheduleType?: string;
};

/** POST admin-panel medicine sync (full URL + x-admin-panel-key from env; not the panel API origin). */
export const postAdminPanelMedicineSync = async (
  payload: AdminPanelMedicineSyncPayload,
) => {
  const url = process.env.REACT_APP_ADMIN_PANEL_MEDICINE_SYNC_URL?.trim();
  const key = process.env.REACT_APP_ADMIN_PANEL_KEY?.trim();
  if (!url || !key) {
    return Promise.reject(
      new Error(
        "Admin panel medicine sync is not configured. Set REACT_APP_ADMIN_PANEL_MEDICINE_SYNC_URL and REACT_APP_ADMIN_PANEL_KEY in .env",
      ),
    );
  }
  return axios.post(url, payload, {
    headers: {
      "Content-Type": "application/json",
      "x-admin-panel-key": key,
    },
  });
};

export type AdminPanelMedicineRemapPayload = {
  fromId: string;
  toId: string;
};

const getAdminPanelKey = () =>
  (process.env.REACT_APP_ADMIN_PANEL_KEY || "").trim();

const getAdminPanelMedicineSyncUrl = () =>
  (process.env.REACT_APP_ADMIN_PANEL_MEDICINE_SYNC_URL || "").trim();

/** Prefer explicit remap URL; otherwise derive from the medicine sync URL. */
const resolveAdminPanelRemapUrl = (
  explicitUrl: string | undefined,
  syncSuffix: "remap-stock" | "remap-transactions",
) => {
  const explicit = (explicitUrl || "").trim();
  if (explicit) return explicit;
  const syncUrl = getAdminPanelMedicineSyncUrl();
  if (!syncUrl) return "";
  if (/\/medicine\/sync\/?$/i.test(syncUrl)) {
    return syncUrl.replace(/\/medicine\/sync\/?$/i, `/medicine/${syncSuffix}`);
  }
  return "";
};

/** POST admin-panel Phase 2 stock/MPD remap for a mapped draft → target. */
export const postAdminPanelRemapStock = async (
  payload: AdminPanelMedicineRemapPayload,
) => {
  // CRA only inlines static process.env.REACT_APP_* access — do not use process.env[key].
  const url = resolveAdminPanelRemapUrl(
    process.env.REACT_APP_ADMIN_PANEL_REMAP_STOCK_URL,
    "remap-stock",
  );
  const key = getAdminPanelKey();
  if (!url || !key) {
    return Promise.reject(
      new Error(
        "Admin panel stock remap is not configured. Set REACT_APP_ADMIN_PANEL_REMAP_STOCK_URL (or REACT_APP_ADMIN_PANEL_MEDICINE_SYNC_URL) and REACT_APP_ADMIN_PANEL_KEY in .env, then restart the panel frontend.",
      ),
    );
  }
  return axios.post(url, payload, {
    headers: {
      "Content-Type": "application/json",
      "x-admin-panel-key": key,
    },
  });
};

/** POST admin-panel Phase 3 transactional remap for a mapped draft → target. */
export const postAdminPanelRemapTransactions = async (
  payload: AdminPanelMedicineRemapPayload,
) => {
  const url = resolveAdminPanelRemapUrl(
    process.env.REACT_APP_ADMIN_PANEL_REMAP_TRANSACTIONS_URL,
    "remap-transactions",
  );
  const key = getAdminPanelKey();
  if (!url || !key) {
    return Promise.reject(
      new Error(
        "Admin panel transactional remap is not configured. Set REACT_APP_ADMIN_PANEL_REMAP_TRANSACTIONS_URL (or REACT_APP_ADMIN_PANEL_MEDICINE_SYNC_URL) and REACT_APP_ADMIN_PANEL_KEY in .env, then restart the panel frontend.",
      ),
    );
  }
  return axios.post(url, payload, {
    headers: {
      "Content-Type": "application/json",
      "x-admin-panel-key": key,
    },
  });
};

export const putApi = async (
  url: string,
  apiData: any,
  flag?: boolean,
  extraHeaders?: Record<string, string>,
) => {
  try {
    const base = apiConfig(flag);
    const headers = {
      ...(base.headers ?? {}),
      ...(extraHeaders ?? {}),
    };
    return await axios.put(`${endPoint}${url}`, apiData, {
      ...base,
      headers,
    });
  } catch (error) {
    return Promise.reject(enrichNetworkError(error, flag));
  }
};

export const deleteApi = async (url: string) => {
  try {
    return await axios.delete(`${endPoint}${url}`, apiConfig());
  } catch (error) {
    return Promise.reject(error);
  }
};

export const deleteApiWithData = async (url: string, apiData?: any) => {
  try {
    return await axios.delete(`${endPoint}${url}`, {
      data: apiData,
      ...apiConfig(),
    });
  } catch (error) {
    return Promise.reject(error);
  }
};

export const putApiNoHeader = async (url: string, apiData: any) => {
  if (localStorage.getItem("accessToken")) {
    try {
      return await axios.put(`${endPoint}${url}`, apiData, {
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`,
        },
      });
    } catch (error) {
      return Promise.reject(error);
    }
  } else {
    // If there's no access token, return an error response or handle it as needed.
    return Promise.reject("No access token available");
  }
};

// export const getApiExcel = async (url?: string, params?: any) => {
// 	const configData = await apiConfig();
// 	try {
// 		return axios.get(`${endPoint}${url}`, {
// 			params: params,
// 			responseType: 'arraybuffer',
// 			...configData,
// 		});
// 	} catch (error) {
// 		return Promise.reject(error);
// 	}
// };

// export const getApiPDF = async (url?: string, params?: any) => {
// 	const configData: any = await apiConfig();
// 	configData.headers['responseType'] = 'blob';
// 	try {
// 		return axios.get(`${endPoint}${url}`, {
// 			params: params,
// 			...configData,
// 		});
// 	} catch (error) {
// 		return Promise.reject(error);
// 	}
// };

// export const getApiCSV = async (url?: string, params?: any) => {
// 	const configData = await apiConfig();
// 	try {
// 		return axios.get(`${endPoint}${url}`, {
// 			params: params,
// 			responseType: 'arraybuffer',
// 			...configData,
// 		});
// 	} catch (error) {
// 		return Promise.reject(error);
// 	}
// };
